import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Chip,
  Collapse,
  Drawer,
  Fab,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Snackbar,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  ArrowDropDown,
  ArrowDropUp,
  ChevronLeft,
  ChevronRight,
  Close as CloseIcon,
  ExpandMore,
  FilterList,
  OpenInNew as OpenInNewIcon,
  Save as SaveIcon,
  Search,
  UnfoldMore,
} from '@mui/icons-material';
import { format } from 'date-fns';
import { NewsCluster } from '../types/types';
import { SentimentHistogram } from '../components/sentiment/SentimentHistogram';
import { AnalyticProfileContext, AnalyticProfileContextType } from '../contexts/AnalyticProfileContext';
import { NotebooksContext } from '../contexts/NotebooksContext';
import { RadarArticleTypeChipsRow } from '../components/radar/RadarTableCells';
import { NotebookPickerList } from '../components/notebook/NotebookPickerList';
import { radarLookbackDaysFromProfile } from '../utils/radarLookback';
import { useRadarPage, RadarClusterKind, RadarSortField } from '../data/useRadarPage';
import {
  ArticleKindFilter,
  nextSort,
  parseArticlesFilters,
  serializeArticlesFilters,
  toggleValue,
  useRadarSearchInput,
  useRadarUrlFilters,
} from '../routing/radarUrlState';

const MAX_CLUSTERS_PER_SAVE = 10;

type FeedOrigin = 'custom' | 'relevant';

export type MergedFeedRow = {
  rowId: string;
  origin: FeedOrigin;
  cluster: NewsCluster;
};

type DrawerMode = 'closed' | 'filters';

function getClusterTypes(cluster: NewsCluster): string[] {
  const types: string[] = [];
  if (cluster.cluster_entry_type) types.push(cluster.cluster_entry_type);
  if (cluster.cluster_content_category) types.push(cluster.cluster_content_category);
  types.push(cluster.cluster_issue_type || 'N/A');
  return types;
}

/** Source-kind filter (ArticleKindFilter) replaces the old separate Custom Sources / Relevant Articles tabs. */
function kindToTypes(kind: ArticleKindFilter): RadarClusterKind[] {
  switch (kind) {
    case 'custom':
      return ['manual_source'];
    case 'relevant':
      return ['single_article'];
    default:
      return ['manual_source', 'single_article'];
  }
}

const LayoutRadarAdditionalFeeds = () => {
  const { selectedProfileId, selectedProfile, selectionRevision } = useContext<AnalyticProfileContextType>(AnalyticProfileContext);

  const radarLookbackDays = useMemo(
    () => radarLookbackDaysFromProfile(selectedProfile),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentional narrow deps: only time_window_hours matters, profile object identity may change
    [selectedProfile?.id, selectedProfile?.time_window_hours],
  );
  const {
    notebooks: notebooksFromContext,
    isLoading: notebooksLoading,
    addNotebook: createNotebook,
    addClustersToNotebook: addClustersToNotebookFn,
    getActiveNotebooks: getActiveNotebooksFn,
    fetchNotebooks,
  } = useContext(NotebooksContext);

  // Filters, sort and page live in the query string (shareable, reload- and Back-friendly).
  // Discrete changes push a history entry; a search pushes once, then refines in place (debounced).
  const { filters, update } = useRadarUrlFilters(parseArticlesFilters, serializeArticlesFilters);
  const setUrlQuery = useCallback((q: string, opts: { replace: boolean }) => update({ q }, opts), [update]);
  const [searchTerm, setSearchTerm] = useRadarSearchInput(filters.q, setUrlQuery);
  const {
    kind: kindFilter,
    sources: sourceFilter,
    entryTypes: entryTypeFilter,
    issueTypes: issueTypeFilter,
    contentCategories: contentCategoryFilter,
    page: currentPage,
  } = filters;
  const sortField: RadarSortField | null = filters.sort?.field ?? null;
  const sortOrder = filters.sort?.order ?? null;
  const setKindFilter = (kind: ArticleKindFilter) => update({ kind });
  // Source filter is article-level; backend filters at cluster level so it stays client-side,
  // applied to the current page only (keeps the page, as before).
  const setSourceFilter = (sources: string[]) => update({ sources }, { keepPage: true });
  const setEntryTypeFilter = (entryTypes: string[]) => update({ entryTypes });
  const setIssueTypeFilter = (issueTypes: string[]) => update({ issueTypes });
  const setContentCategoryFilter = (contentCategories: string[]) => update({ contentCategories });
  const setCurrentPage = (page: number) => update({ page });

  const [drawerMode, setDrawerMode] = useState<DrawerMode>('closed');
  const [selectedRows, setSelectedRows] = useState<MergedFeedRow[]>([]);

  const [sourceSearchTerm, setSourceSearchTerm] = useState('');
  const [entryTypeSearchTerm, setEntryTypeSearchTerm] = useState('');
  const [issueTypeSearchTerm, setIssueTypeSearchTerm] = useState('');
  const [contentCategorySearchTerm, setContentCategorySearchTerm] = useState('');

  const itemsPerPage = 10;

  const [saveDrawerOpen, setSaveDrawerOpen] = useState(false);
  const [saveNotebookTab, setSaveNotebookTab] = useState(0);
  const [selectedNotebookId, setSelectedNotebookId] = useState('');
  const [newNotebookName, setNewNotebookName] = useState('');
  const [newNotebookDescription, setNewNotebookDescription] = useState('');
  const [isSavingToNotebook, setIsSavingToNotebook] = useState(false);
  const [saveToNotebookError, setSaveToNotebookError] = useState('');
  const [saveToNotebookSuccess, setSaveToNotebookSuccess] = useState('');
  const [showSaveToNotebookAlert, setShowSaveToNotebookAlert] = useState(false);

  // An explicit analytic profile switch keeps the filters but returns to page 1. selectionRevision only
  // moves on a user choice, so the default pick / localStorage restore on login keeps a shared link's page.
  const prevSelectionRevisionRef = useRef(selectionRevision);
  useEffect(() => {
    if (prevSelectionRevisionRef.current === selectionRevision) return;
    prevSelectionRevisionRef.current = selectionRevision;
    update({ page: 1 }, { replace: true });
  }, [selectionRevision, update]);

  const radarPage = useRadarPage({
    profileId: selectedProfileId ?? null,
    types: kindToTypes(kindFilter),
    page: currentPage,
    pageSize: itemsPerPage,
    q: filters.q || undefined,
    entryTypes: entryTypeFilter.length ? entryTypeFilter : undefined,
    issueTypes: issueTypeFilter.length ? issueTypeFilter : undefined,
    contentCategories: contentCategoryFilter.length ? contentCategoryFilter : undefined,
    sort: sortField,
    order: sortOrder,
    lookbackDays: radarLookbackDays,
  });

  // Map server items into the same MergedFeedRow shape the rest of the
  // component already speaks. Origin comes from cluster.type so the row id is
  // stable across kind-filter changes.
  const baseRows: MergedFeedRow[] = useMemo(
    () =>
      radarPage.items.map((cluster) => {
        const origin: FeedOrigin = cluster.type === 'manual_source' ? 'custom' : 'relevant';
        const id = cluster.cluster_id ?? cluster.title;
        return { rowId: `${origin}::${id}`, origin, cluster };
      }),
    [radarPage.items],
  );

  // Source filter applies on top of the server page (article-level facet).
  const filteredRows = useMemo(() => {
    if (sourceFilter.length === 0) return baseRows;
    return baseRows.filter((row) =>
      row.cluster.articles.some((a) => sourceFilter.includes(a.source)),
    );
  }, [baseRows, sourceFilter]);

  // Server already sorted and paginated. These names exist for compatibility
  // with the existing JSX below.
  const sortedRows = filteredRows;
  const displayedRows = filteredRows;

  const getSortIcon = (field: RadarSortField) => {
    if (sortField === field && sortOrder === 'asc') {
      return <ArrowDropUp fontSize="small" style={{ marginLeft: 4 }} />;
    }
    if (sortField === field && sortOrder === 'desc') {
      return <ArrowDropDown fontSize="small" style={{ marginLeft: 4 }} />;
    }
    return <UnfoldMore fontSize="small" style={{ marginLeft: 4 }} />;
  };

  const handleSort = (field: RadarSortField) => {
    update({ sort: nextSort(filters.sort, field) });
  };

  const allSources = useMemo(() => {
    const s = new Set<string>();
    baseRows.forEach(({ cluster }) => {
      cluster.articles.forEach((a) => {
        if (a.source) s.add(a.source);
      });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
  }, [baseRows]);

  const filteredSources = useMemo(
    () =>
      allSources
        .filter((x) => x.toLowerCase().includes(sourceSearchTerm.toLowerCase()))
        .sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' })),
    [allSources, sourceSearchTerm]
  );

  const allEntryTypes = useMemo(() => {
    const s = new Set<string>();
    baseRows.forEach(({ cluster }) => {
      cluster.articles.forEach((a) => {
        if (a.entry_type) s.add(a.entry_type);
      });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
  }, [baseRows]);

  const filteredEntryTypes = useMemo(
    () =>
      allEntryTypes
        .filter((t) => t.toLowerCase().includes(entryTypeSearchTerm.toLowerCase()))
        .sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' })),
    [allEntryTypes, entryTypeSearchTerm]
  );

  const allIssueTypes = useMemo(() => {
    const s = new Set<string>();
    baseRows.forEach(({ cluster }) => {
      cluster.articles.forEach((a) => {
        if (a.issue_type) s.add(a.issue_type);
      });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
  }, [baseRows]);

  const filteredIssueTypes = useMemo(
    () =>
      allIssueTypes
        .filter((t) => t.toLowerCase().includes(issueTypeSearchTerm.toLowerCase()))
        .sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' })),
    [allIssueTypes, issueTypeSearchTerm]
  );

  const allContentCategories = useMemo(() => {
    const s = new Set<string>();
    baseRows.forEach(({ cluster }) => {
      cluster.articles.forEach((a) => {
        if (a.content_category) s.add(a.content_category);
      });
    });
    return Array.from(s).sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' }));
  }, [baseRows]);

  const filteredContentCategories = useMemo(
    () =>
      allContentCategories
        .filter((c) => c.toLowerCase().includes(contentCategorySearchTerm.toLowerCase()))
        .sort((a, b) => a.localeCompare(b, 'sl', { sensitivity: 'base' })),
    [allContentCategories, contentCategorySearchTerm]
  );

  const formatPublished = (iso?: string) => {
    if (!iso) return '-';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '-';
    return format(d, 'dd/MM/yyyy');
  };

  const toggleRow = (row: MergedFeedRow) => {
    setSelectedRows((prev) =>
      prev.some((r) => r.rowId === row.rowId) ? prev.filter((r) => r.rowId !== row.rowId) : [...prev, row]
    );
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedRows([...sortedRows]);
    } else {
      setSelectedRows([]);
    }
  };

  const closeDrawer = () => {
    setDrawerMode('closed');
  };

  const openFilters = () => {
    setDrawerMode('filters');
  };

  const activeNotebooks = useMemo(() => getActiveNotebooksFn(), [getActiveNotebooksFn]);

  const openAddToNotebookDrawer = () => {
    setSaveNotebookTab(0);
    setSaveToNotebookError('');
    setSaveToNotebookSuccess('');
    setSaveDrawerOpen(true);
    void fetchNotebooks();
  };

  const closeAddToNotebookDrawer = () => {
    setSaveDrawerOpen(false);
    setNewNotebookName('');
    setNewNotebookDescription('');
    setSelectedNotebookId('');
  };

  const handleSaveClustersToNotebook = async () => {
    const clusters = selectedRows.map((r) => r.cluster);
    setSaveToNotebookError('');
    setSaveToNotebookSuccess('');
    setIsSavingToNotebook(true);
    try {
      if (clusters.length === 0) {
        setSaveToNotebookError('No articles selected');
        setShowSaveToNotebookAlert(true);
        return;
      }
      if (clusters.length > MAX_CLUSTERS_PER_SAVE) {
        setSaveToNotebookError(
          `You can only add up to ${MAX_CLUSTERS_PER_SAVE} clusters at once. Please select fewer rows.`
        );
        setShowSaveToNotebookAlert(true);
        return;
      }
      if (saveNotebookTab === 1) {
        if (!newNotebookName.trim()) {
          setSaveToNotebookError('Please enter a name for the new notebook');
          setShowSaveToNotebookAlert(true);
          return;
        }
        const created = await createNotebook({
          name: newNotebookName.trim(),
          description: newNotebookDescription.trim() || 'Created from Articles (radar)',
          clusters: [...clusters],
          status: 'active',
          visibility: 'private',
        });
        if (created) {
          setSaveToNotebookSuccess(`Saved to new notebook "${newNotebookName.trim()}"`);
          closeAddToNotebookDrawer();
          setSelectedRows([]);
        } else {
          setSaveToNotebookError('Failed to create notebook');
        }
      } else {
        if (!selectedNotebookId) {
          setSaveToNotebookError('Please select a notebook');
          setShowSaveToNotebookAlert(true);
          return;
        }
        const ok = await addClustersToNotebookFn(selectedNotebookId, clusters);
        const nb = notebooksFromContext.find((n) => n.id === selectedNotebookId);
        if (ok) {
          setSaveToNotebookSuccess(`Added to notebook "${nb?.name ?? 'notebook'}"`);
          closeAddToNotebookDrawer();
          setSelectedRows([]);
        } else {
          setSaveToNotebookError('Failed to add to notebook');
        }
      }
      setShowSaveToNotebookAlert(true);
    } catch (e: unknown) {
      const msg = e instanceof Error ? e.message : 'An error occurred while saving';
      setSaveToNotebookError(msg);
      setShowSaveToNotebookAlert(true);
    } finally {
      setIsSavingToNotebook(false);
    }
  };

  const filterBadgeInvisible =
    kindFilter === 'all' &&
    sourceFilter.length === 0 &&
    entryTypeFilter.length === 0 &&
    issueTypeFilter.length === 0 &&
    contentCategoryFilter.length === 0;

  const toggleSource = (s: string) => setSourceFilter(toggleValue(sourceFilter, s));
  const toggleEntry = (t: string) => setEntryTypeFilter(toggleValue(entryTypeFilter, t));
  const toggleIssue = (t: string) => setIssueTypeFilter(toggleValue(issueTypeFilter, t));
  const toggleCategory = (t: string) => setContentCategoryFilter(toggleValue(contentCategoryFilter, t));

  // Server-driven page count and total. radarPage.total counts everything that
  // matches the backend filters; the source-kind filter (article-level, applied
  // locally) doesn't reduce it, which is fine — the badge is informational.
  const totalPages = radarPage.pageCount;
  const totalForDisplay = radarPage.total;

  return (
    <Box display="flex" flexDirection="column" minHeight="100vh" bgcolor="#0a0a0a">
      <Box borderBottom={1} borderColor="#27272a">
        <Box display="flex" alignItems="center" padding={2} gap={1}>
          <TextField
            variant="outlined"
            placeholder="Search clusters..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            fullWidth
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: '#a1a1aa' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              maxWidth: 720,
              flex: 1,
              '& .MuiOutlinedInput-root': {
                bgcolor: '#18181b',
                color: '#fafafa',
                '& fieldset': { borderColor: '#3f3f46' },
                '&:hover fieldset': { borderColor: '#a1a1aa' },
                '&.Mui-focused fieldset': { borderColor: '#60a5fa' },
              },
              '& .MuiInputBase-input::placeholder': { color: '#a1a1aa', opacity: 1 },
            }}
          />
          <Badge color="primary" variant="dot" invisible={filterBadgeInvisible}>
            <Tooltip title="Filters">
              <IconButton
                onClick={openFilters}
                aria-label="Open filters"
                sx={{
                  color: '#fafafa',
                  border: '1px solid #3f3f46',
                  borderRadius: 1,
                  '&:hover': { bgcolor: '#27272a', borderColor: '#a1a1aa' },
                }}
              >
                <FilterList />
              </IconButton>
            </Tooltip>
          </Badge>
        </Box>
      </Box>

      <Box flexGrow={1} padding={1} sx={{ pb: 12 }}>
        <TableContainer component={Paper} sx={{ bgcolor: '#18181b', border: '1px solid #27272a' }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: '#09090b', borderBottom: '2px solid #27272a' }}>
                <TableCell align="center" sx={{ width: '3%', color: '#fafafa', borderColor: '#27272a' }}>
                  <Checkbox
                    indeterminate={
                      selectedRows.length > 0 && selectedRows.length < sortedRows.length
                    }
                    checked={sortedRows.length > 0 && selectedRows.length === sortedRows.length}
                    onChange={handleSelectAll}
                    sx={{
                      color: '#a1a1aa',
                      '&.Mui-checked': { color: '#60a5fa' },
                      '&.MuiCheckbox-indeterminate': { color: '#60a5fa' },
                    }}
                  />
                </TableCell>
                <TableCell align="center" sx={{ color: '#fafafa', borderColor: '#27272a' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Source
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ color: '#fafafa', borderColor: '#27272a' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Title
                  </Typography>
                </TableCell>
                <TableCell
                  align="center"
                  sx={{
                    color: '#fafafa',
                    borderColor: '#27272a',
                    minWidth: 300,
                    width: '32%',
                    maxWidth: 440,
                  }}
                >
                  <Tooltip
                    title="Format (blue) = content type · Category (purple) = editorial category · SDG (green) = Sustainable Development Goal tag"
                    arrow
                  >
                    <Typography
                      component="span"
                      variant="body2"
                      sx={{
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap',
                        cursor: 'help',
                        borderBottom: '1px dotted #71717a',
                      }}
                    >
                      Article types
                    </Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ color: '#fafafa', borderColor: '#27272a' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Sentiment
                  </Typography>
                </TableCell>
                <TableCell align="center" sx={{ color: '#fafafa', borderColor: '#27272a', cursor: 'pointer' }}>
                  <Box display="flex" alignItems="center" justifyContent="center" onClick={() => handleSort('published_at')}>
                    <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                      Date
                    </Typography>
                    {getSortIcon('published_at')}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ width: '3%', color: '#fafafa', borderColor: '#27272a' }}>
                  <Typography variant="body2" sx={{ fontWeight: 'bold', whiteSpace: 'nowrap' }}>
                    Link
                  </Typography>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {displayedRows.map((row, index) => {
                const { cluster } = row;
                return (
                  <TableRow
                    key={row.rowId}
                    sx={{
                      bgcolor: index % 2 === 0 ? '#09090b' : '#18181b',
                      '&:hover': { bgcolor: '#27272a' },
                    }}
                  >
                    <TableCell align="center" sx={{ borderColor: '#27272a' }}>
                      <Checkbox
                        checked={selectedRows.some((r) => r.rowId === row.rowId)}
                        onChange={() => toggleRow(row)}
                        sx={{ color: '#a1a1aa', '&.Mui-checked': { color: '#60a5fa' } }}
                      />
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#fafafa', borderColor: '#27272a', maxWidth: 120 }}>
                      <Typography variant="body2" noWrap title={cluster.articles[0]?.source}>
                        {cluster.articles[0]?.source ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderColor: '#27272a',
                        maxWidth: 320,
                        py: 0.75,
                        verticalAlign: 'middle',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{
                          color: '#fafafa',
                          fontWeight: 700,
                          lineHeight: 1.35,
                          whiteSpace: 'normal',
                          wordBreak: 'break-word',
                          overflowWrap: 'anywhere',
                        }}
                      >
                        {cluster.articles[0]?.article_title ?? '—'}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        borderColor: '#27272a',
                        py: 0.75,
                        verticalAlign: 'middle',
                        minWidth: 300,
                        width: '32%',
                        maxWidth: 440,
                      }}
                    >
                      <RadarArticleTypeChipsRow types={getClusterTypes(cluster)} max={12} layout="wrap" />
                    </TableCell>
                    <TableCell align="center" sx={{ borderColor: '#27272a', py: 0.5 }}>
                      <SentimentHistogram articles={cluster.articles} compact />
                    </TableCell>
                    <TableCell align="center" sx={{ color: '#fafafa', borderColor: '#27272a' }}>
                      {formatPublished(cluster.articles[0]?.published_at)}
                    </TableCell>
                    <TableCell align="center" sx={{ borderColor: '#27272a' }}>
                      {cluster.articles[0]?.url ? (
                        <Tooltip title="Open article in new tab">
                          <IconButton
                            component="a"
                            href={cluster.articles[0].url}
                            target="_blank"
                            rel="noopener noreferrer"
                            size="small"
                            aria-label="Open article in new tab"
                            sx={{ color: '#a1a1aa', '&:hover': { color: '#60a5fa' } }}
                          >
                            <OpenInNewIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      ) : (
                        <Typography variant="caption" sx={{ color: '#52525b' }}>
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

      </Box>

      <Box
        sx={{
          position: 'fixed',
          left: 16,
          right: 16,
          bottom: { xs: 20, sm: 28 },
          zIndex: (theme) => theme.zIndex.appBar,
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'space-between',
          gap: 2,
          pointerEvents: 'none',
          '& .MuiIconButton-root': { pointerEvents: 'auto' },
          pr: selectedRows.length > 0 ? { xs: 'min(52vw, 220px)', sm: 'min(48vw, 240px)' } : 0,
        }}
      >
        <Typography sx={{ color: '#fafafa', fontSize: '0.875rem', flexShrink: 0, pb: 0.25 }}>
          Showing {totalForDisplay === 0 ? 0 : (currentPage - 1) * itemsPerPage + 1} to{' '}
          {Math.min(currentPage * itemsPerPage, totalForDisplay)} of {totalForDisplay} results
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 0.5, flexShrink: 0 }}>
          <IconButton
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            sx={{ color: currentPage === 1 ? '#52525b' : '#fafafa' }}
            aria-label="Previous page"
          >
            <ChevronLeft />
          </IconButton>
          <IconButton
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage >= totalPages || totalForDisplay === 0}
            sx={{ color: currentPage >= totalPages || totalForDisplay === 0 ? '#52525b' : '#fafafa' }}
            aria-label="Next page"
          >
            <ChevronRight />
          </IconButton>
        </Box>
      </Box>

      <Fab
        variant="extended"
        onClick={openAddToNotebookDrawer}
        disabled={selectedRows.length === 0}
        aria-label="Add to notebook"
        sx={{
          position: 'fixed',
          right: { xs: 20, sm: 28 },
          bottom: { xs: 20, sm: 28 },
          zIndex: (theme) => theme.zIndex.drawer - 1,
          bgcolor: '#60a5fa',
          color: '#0a0a0a',
          boxShadow: '0 8px 24px rgba(96, 165, 250, 0.35)',
          textTransform: 'none',
          fontWeight: 'bold',
          px: 2,
          '&:hover': { bgcolor: '#3b82f6' },
          '&.Mui-disabled': {
            bgcolor: '#3f3f46',
            color: '#a1a1aa',
            boxShadow: 'none',
          },
        }}
      >
        <SaveIcon sx={{ mr: 1 }} />
        Add to notebook
      </Fab>

      <Drawer
        anchor="right"
        open={drawerMode !== 'closed'}
        onClose={closeDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: '50vw' },
            maxWidth: { xs: '100vw', sm: '50vw' },
            bgcolor: '#18181b',
            borderLeft: '1px solid #27272a',
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #27272a',
              bgcolor: '#09090b',
            }}
          >
            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 'bold', pr: 1 }}>
              Filters
            </Typography>
            <IconButton onClick={closeDrawer} aria-label="Close" sx={{ color: '#fafafa' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {drawerMode === 'filters' && (
              <Box sx={{ p: 1 }}>
                {/*
                  Source-kind filter — replaces the old separate Custom Sources
                  and Relevant Articles tabs. "All" is the default and serves the
                  union of both kinds in one paged feed.
                */}
                <Accordion
                  disableGutters
                  defaultExpanded
                  sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}
                >
                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">Source kind</Typography>
                      {kindFilter !== 'all' && (
                        <Chip size="small" label={kindFilter === 'custom' ? 'Custom' : 'Relevant'} sx={{ height: 22 }} />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <List dense>
                      {(
                        [
                          { value: 'all' as const, label: 'All articles' },
                          { value: 'custom' as const, label: 'Custom sources only' },
                          { value: 'relevant' as const, label: 'Relevant articles only' },
                        ]
                      ).map(({ value, label }) => (
                        <ListItem
                          key={value}
                          dense
                          button
                          onClick={() => setKindFilter(value)}
                          sx={{ '&:hover': { bgcolor: '#27272a' } }}
                        >
                          <Checkbox
                            edge="start"
                            checked={kindFilter === value}
                            tabIndex={-1}
                            disableRipple
                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                          />
                          <ListItemText primary={label} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Accordion disableGutters sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">Publication source</Typography>
                      {sourceFilter.length > 0 && <Chip size="small" label={sourceFilter.length} sx={{ height: 22 }} />}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                      <Button size="small" onClick={() => setSourceFilter([])} sx={{ color: '#60a5fa' }}>
                        Clear
                      </Button>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Search sources..."
                      value={sourceSearchTerm}
                      onChange={(e) => setSourceSearchTerm(e.target.value)}
                      fullWidth
                      sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#09090b',
                          color: '#fafafa',
                          '& fieldset': { borderColor: '#3f3f46' },
                        },
                      }}
                    />
                    <List dense sx={{ maxHeight: 220, overflow: 'auto' }}>
                      {filteredSources.map((s) => (
                        <ListItem key={s} dense button onClick={() => toggleSource(s)} sx={{ '&:hover': { bgcolor: '#27272a' } }}>
                          <Checkbox
                            edge="start"
                            checked={sourceFilter.includes(s)}
                            tabIndex={-1}
                            disableRipple
                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                          />
                          <ListItemText primary={s} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Accordion disableGutters sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">Format</Typography>
                      {entryTypeFilter.length > 0 && <Chip size="small" label={entryTypeFilter.length} sx={{ height: 22 }} />}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                      <Button size="small" onClick={() => setEntryTypeFilter([])} sx={{ color: '#60a5fa' }}>
                        Clear
                      </Button>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Search formats..."
                      value={entryTypeSearchTerm}
                      onChange={(e) => setEntryTypeSearchTerm(e.target.value)}
                      fullWidth
                      sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#09090b',
                          color: '#fafafa',
                          '& fieldset': { borderColor: '#3f3f46' },
                        },
                      }}
                    />
                    <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {filteredEntryTypes.map((t) => (
                        <ListItem key={t} dense button onClick={() => toggleEntry(t)} sx={{ '&:hover': { bgcolor: '#27272a' } }}>
                          <Checkbox
                            edge="start"
                            checked={entryTypeFilter.includes(t)}
                            tabIndex={-1}
                            disableRipple
                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                          />
                          <ListItemText primary={t} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Accordion disableGutters sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">Category</Typography>
                      {contentCategoryFilter.length > 0 && (
                        <Chip size="small" label={contentCategoryFilter.length} sx={{ height: 22 }} />
                      )}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                      <Button size="small" onClick={() => setContentCategoryFilter([])} sx={{ color: '#60a5fa' }}>
                        Clear
                      </Button>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Search categories..."
                      value={contentCategorySearchTerm}
                      onChange={(e) => setContentCategorySearchTerm(e.target.value)}
                      fullWidth
                      sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#09090b',
                          color: '#fafafa',
                          '& fieldset': { borderColor: '#3f3f46' },
                        },
                      }}
                    />
                    <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {filteredContentCategories.map((t) => (
                        <ListItem key={t} dense button onClick={() => toggleCategory(t)} sx={{ '&:hover': { bgcolor: '#27272a' } }}>
                          <Checkbox
                            edge="start"
                            checked={contentCategoryFilter.includes(t)}
                            tabIndex={-1}
                            disableRipple
                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                          />
                          <ListItemText primary={t} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
                <Accordion disableGutters sx={{ bgcolor: '#18181b', color: '#fafafa', '&:before': { display: 'none' } }}>
                  <AccordionSummary expandIcon={<ExpandMore sx={{ color: '#fafafa' }} />}>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Typography fontWeight="bold">SDG topic</Typography>
                      {issueTypeFilter.length > 0 && <Chip size="small" label={issueTypeFilter.length} sx={{ height: 22 }} />}
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box display="flex" justifyContent="flex-end" mb={1}>
                      <Button size="small" onClick={() => setIssueTypeFilter([])} sx={{ color: '#60a5fa' }}>
                        Clear
                      </Button>
                    </Box>
                    <TextField
                      size="small"
                      placeholder="Search SDG topics..."
                      value={issueTypeSearchTerm}
                      onChange={(e) => setIssueTypeSearchTerm(e.target.value)}
                      fullWidth
                      sx={{
                        mb: 1,
                        '& .MuiOutlinedInput-root': {
                          bgcolor: '#09090b',
                          color: '#fafafa',
                          '& fieldset': { borderColor: '#3f3f46' },
                        },
                      }}
                    />
                    <List dense sx={{ maxHeight: 200, overflow: 'auto' }}>
                      {filteredIssueTypes.map((t) => (
                        <ListItem key={t} dense button onClick={() => toggleIssue(t)} sx={{ '&:hover': { bgcolor: '#27272a' } }}>
                          <Checkbox
                            edge="start"
                            checked={issueTypeFilter.includes(t)}
                            tabIndex={-1}
                            disableRipple
                            sx={{ color: '#fafafa', '&.Mui-checked': { color: '#60a5fa' } }}
                          />
                          <ListItemText primary={t} />
                        </ListItem>
                      ))}
                    </List>
                  </AccordionDetails>
                </Accordion>
              </Box>
            )}
          </Box>
        </Box>
      </Drawer>

      <Drawer
        anchor="right"
        open={saveDrawerOpen}
        onClose={closeAddToNotebookDrawer}
        PaperProps={{
          sx: {
            width: { xs: '100vw', sm: '50vw' },
            maxWidth: { xs: '100vw', sm: '50vw' },
            bgcolor: '#18181b',
            borderLeft: '1px solid #27272a',
            zIndex: (theme) => theme.zIndex.drawer + 2,
          },
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', minHeight: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              borderBottom: '1px solid #27272a',
              bgcolor: '#09090b',
              flexShrink: 0,
            }}
          >
            <Typography variant="h6" sx={{ color: '#fafafa', fontWeight: 'bold' }}>
              Add to notebook
            </Typography>
            <IconButton onClick={closeAddToNotebookDrawer} aria-label="Close" sx={{ color: '#fafafa' }}>
              <CloseIcon />
            </IconButton>
          </Box>
          <Box
            sx={{
              flex: 1,
              minHeight: 0,
              display: 'flex',
              flexDirection: 'column',
              p: 2,
              overflow: 'hidden',
            }}
          >
            {selectedRows.length > MAX_CLUSTERS_PER_SAVE && (
              <Alert severity="warning" sx={{ mb: 2, flexShrink: 0 }}>
                You can add at most {MAX_CLUSTERS_PER_SAVE} clusters at once. Deselect some rows to continue.
              </Alert>
            )}
            <Tabs
              value={saveNotebookTab}
              onChange={(_, v) => setSaveNotebookTab(v)}
              sx={{
                mb: 2,
                flexShrink: 0,
                borderBottom: '1px solid #27272a',
                '& .MuiTab-root': { color: '#a1a1aa', textTransform: 'none' },
                '& .Mui-selected': { color: '#fafafa' },
                '& .MuiTabs-indicator': { bgcolor: '#60a5fa' },
              }}
            >
              <Tab label="Existing notebook" />
              <Tab label="New notebook" />
            </Tabs>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
              {saveNotebookTab === 0 ? (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
                  <NotebookPickerList
                    notebooks={activeNotebooks}
                    selectedId={selectedNotebookId}
                    onSelect={setSelectedNotebookId}
                    loading={notebooksLoading}
                    emptyMessage={
                      <>No active notebooks. Switch to &quot;New notebook&quot; to create one.</>
                    }
                  />
                </Box>
              ) : (
                <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
                  <TextField
                    label="Notebook name"
                    fullWidth
                    value={newNotebookName}
                    onChange={(e) => setNewNotebookName(e.target.value)}
                    sx={{
                      flexShrink: 0,
                      '& .MuiOutlinedInput-root': { color: '#fafafa' },
                      '& .MuiInputLabel-root': { color: '#a1a1aa' },
                    }}
                  />
                  <TextField
                    label="Description (optional)"
                    fullWidth
                    multiline
                    value={newNotebookDescription}
                    onChange={(e) => setNewNotebookDescription(e.target.value)}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'stretch',
                      '& .MuiInputBase-root': {
                        flex: 1,
                        alignItems: 'flex-start',
                        py: 1,
                      },
                      '& textarea': { height: '100% !important', overflow: 'auto !important', boxSizing: 'border-box' },
                      '& .MuiOutlinedInput-root': { color: '#fafafa', height: '100%' },
                      '& .MuiInputLabel-root': { color: '#a1a1aa' },
                    }}
                  />
                </Box>
              )}
            </Box>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              px: 2,
              py: 2,
              borderTop: '1px solid #27272a',
              bgcolor: '#18181b',
              display: 'flex',
              justifyContent: 'flex-end',
            }}
          >
            <Button
              variant="contained"
              onClick={() => void handleSaveClustersToNotebook()}
              disabled={
                isSavingToNotebook ||
                selectedRows.length === 0 ||
                selectedRows.length > MAX_CLUSTERS_PER_SAVE
              }
              startIcon={<SaveIcon />}
              sx={{ bgcolor: '#60a5fa', color: '#0a0a0a', '&:hover': { bgcolor: '#3b82f6' } }}
            >
              {isSavingToNotebook ? 'Saving…' : 'Save'}
            </Button>
          </Box>
        </Box>
      </Drawer>

      <Snackbar
        open={showSaveToNotebookAlert}
        autoHideDuration={6000}
        onClose={() => setShowSaveToNotebookAlert(false)}
        TransitionComponent={Collapse}
      >
        {saveToNotebookError ? (
          <Alert onClose={() => setShowSaveToNotebookAlert(false)} severity="error" sx={{ width: '100%' }}>
            {saveToNotebookError}
          </Alert>
        ) : saveToNotebookSuccess ? (
          <Alert onClose={() => setShowSaveToNotebookAlert(false)} severity="success" sx={{ width: '100%' }}>
            {saveToNotebookSuccess}
          </Alert>
        ) : (
          <></>
        )}
      </Snackbar>
    </Box>
  );
};

export default LayoutRadarAdditionalFeeds;
